/**
 * Onboarding research-profile options — single source of truth for the
 * wizard chips (frontend), the Zod validation of the createWorkspace
 * payload (backend), the seed-content template selection, and the
 * suggested-prompt matrix.
 *
 * Keys are stable snake_case identifiers: they land in
 * `workspace.settings.workspaceContext` and as PostHog person properties
 * (`research_field`, `research_role`, `lab_size`). Labels are display-only
 * and safe to reword. Free-text "other" values are UGC — they are stored
 * in workspaceContext and emitted only through the explicit server-side
 * onboarding option-list feedback event after the PostHog UGC egress check;
 * they are never person properties or application logs.
 *
 * Spec: docs/superpowers/specs/2026-07-05-onboarding-hero-journey-spec.md
 */
export const RESEARCH_FIELD_OPTIONS = [
    { key: 'drug_discovery', label: 'Drug discovery' },
    { key: 'therapeutics', label: 'Therapeutics' },
    { key: 'diagnostics', label: 'Diagnostics' },
    { key: 'agri_environmental', label: 'Agricultural / Environmental' },
    { key: 'synthetic_biology', label: 'Synthetic biology' },
    { key: 'academic_fundamental', label: 'Academic / Fundamental' },
    { key: 'industrial_biotech', label: 'Industrial biotech' },
    { key: 'other', label: 'Other' },
];
export const RESEARCH_ROLE_OPTIONS = [
    { key: 'principal_investigator', label: 'Principal Investigator' },
    { key: 'research_scientist', label: 'Research Scientist' },
    { key: 'lab_manager', label: 'Lab Manager' },
    { key: 'postdoc', label: 'Postdoctoral Researcher' },
    { key: 'phd_student', label: 'PhD Student' },
    { key: 'research_director', label: 'Research Director / CSO' },
    { key: 'data_scientist', label: 'Bioinformatician / Data Scientist' },
    { key: 'other', label: 'Other' },
];
export const LAB_SIZE_OPTIONS = [
    { key: '1_5', label: '1–5' },
    { key: '6_20', label: '6–20' },
    { key: '21_50', label: '21–50' },
    { key: '51_100', label: '51–100' },
    { key: '100_plus', label: '100+' },
];
/**
 * Titles of the field-matched starter protocols the backend seeds. Kept
 * identical to `FIELD_PROTOCOLS[key].title` in the backend seed-templates
 * so the wizard's preview promises exactly what seeding creates. The
 * backend content is the source of truth; this mirror exists only so the
 * frontend preview can show the title before the workspace exists.
 *
 * SSOT lockstep: pinned by the backend test
 * `seed-content/__tests__/seed-templates.test.ts` — a backend title edit
 * that forgets this mirror fails CI there.
 */
export const FIELD_PROTOCOL_TITLES = {
    drug_discovery: '[Example] Dose–response IC50 assay (96-well)',
    therapeutics: '[Example] ELISA-based binding assay for a therapeutic antibody',
    diagnostics: '[Example] qPCR assay validation run',
    agri_environmental: '[Example] Soil DNA extraction for microbiome analysis',
    synthetic_biology: '[Example] Golden Gate assembly (BsaI, one-pot)',
    academic_fundamental: '[Example] Western blot (wet transfer, chemiluminescent)',
    industrial_biotech: '[Example] Fed-batch fermentation batch record (5 L)',
    general: '[Example] Structured lab-notebook entry',
};
/** Resolve the starter-protocol title for a field key (falls back to general). */
export function starterProtocolTitle(fieldKey) {
    if (fieldKey && fieldKey !== 'other' && Object.hasOwn(FIELD_PROTOCOL_TITLES, fieldKey)) {
        return FIELD_PROTOCOL_TITLES[fieldKey];
    }
    return FIELD_PROTOCOL_TITLES.general;
}
/** Prefix on every seeded starter-protocol title (see FIELD_PROTOCOL_TITLES). */
export const SEED_EXAMPLE_TITLE_PREFIX = '[Example] ';
/**
 * Title of the seeded "Start here" welcome note the backend creates.
 * Mirrors the backend `buildStartHereNote` title; pinned by the backend
 * lockstep test so a rename in either place fails CI.
 */
export const SEED_START_HERE_TITLE = 'Start here: welcome to Bower';
/**
 * True when an artifact title is Bower-seeded onboarding content (a starter
 * protocol or the welcome note) rather than the user's own work. Both the
 * backend seeder and the frontend (recents filtering + workspace-stage signal)
 * derive from this single definition so they can't silently drift — a rename
 * that broke it would flip new users onto the wrong personalisation set.
 */
export function isSeededArtifactTitle(title) {
    const t = title ?? '';
    return t.startsWith(SEED_EXAMPLE_TITLE_PREFIX) || t === SEED_START_HERE_TITLE;
}
export const RESEARCH_FIELD_KEYS = RESEARCH_FIELD_OPTIONS.map((o) => o.key);
export const RESEARCH_ROLE_KEYS = RESEARCH_ROLE_OPTIONS.map((o) => o.key);
export const LAB_SIZE_KEYS = LAB_SIZE_OPTIONS.map((o) => o.key);
//# sourceMappingURL=onboardingProfile.js.map